import { useState, useMemo } from "react";
import { Card } from "../ui/Card";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { useDebounce } from "../../hooks/useDebounce";
import {
  Search,
  Filter,
  ArrowUpDown,
  MoreHorizontal,
  Edit2,
  Trash2,
  CheckCircle2,
  XCircle,
  FolderOpen,
} from "lucide-react";
import { TableActionMenu } from "../ui/TableActionMenu";
import { useAuth } from "../../contexts/AuthContext";

export interface Trade {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  thumbnail?: string;
  isActive: boolean;
  order: number;
  coursesCount?: number;
  createdAt: string;
  updatedAt: string;
}

interface TradeTableProps {
  trades: Trade[];
  description?: string;
  isLoading?: boolean;
  onAddClick: () => void;
  onEditClick: (trade: Trade) => void;
  onDeleteClick: (trade: Trade) => void;
  onToggleStatus: (trade: Trade) => void;
}

type SortField = "name" | "order" | "coursesCount" | "createdAt" | "updatedAt";
type SortOrder = "asc" | "desc";

export function TradeTable({
  trades,
  description = "Manage your trade categories.",
  isLoading,
  onAddClick,
  onEditClick,
  onDeleteClick,
  onToggleStatus,
}: TradeTableProps) {
  const { hasPermission } = useAuth();

  const canCreate = hasPermission('trades:create');
  const canUpdate = hasPermission('trades:update');
  const canDelete = hasPermission('trades:delete');
  const canToggle = hasPermission('trades:toggle-active');

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [sortField, setSortField] = useState<SortField>("order");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);

  const [activeMenu, setActiveMenu] = useState<{
    id: string;
    position: { top: number; right: number };
  } | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const filteredAndSortedTrades = useMemo(() => {
    let result = trades.filter((trade) => {
      const matchesSearch =
        trade.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        (trade.description?.toLowerCase() || "").includes(
          debouncedSearchTerm.toLowerCase(),
        );

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && trade.isActive) ||
        (statusFilter === "inactive" && !trade.isActive);

      return matchesSearch && matchesStatus;
    });

    result.sort((a, b) => {
      let valA: any = a[sortField];
      let valB: any = b[sortField];

      if (sortField === "createdAt" || sortField === "updatedAt") {
        valA = new Date(valA).getTime();
        valB = new Date(valB).getTime();
      } else if (typeof valA === "string") {
        valA = valA.toLowerCase();
        valB = (valB as string).toLowerCase();
      }

      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [trades, debouncedSearchTerm, sortField, sortOrder, statusFilter]);

  // Calculate pagination
  const totalItems = filteredAndSortedTrades.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const paginatedTrades = filteredAndSortedTrades.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">
          Trades
        </h2>
        <p className="text-gray-500">{description}</p>
      </div>

      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
        {/* Search input */}
        <div className="w-full xl:w-[400px]">
          <Input
            placeholder="Search trades"
            icon={<Search size={20} />}
            className="bg-white w-full"
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        {/* Filter buttons and primary action */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full xl:w-auto">
          <div className="grid grid-cols-2 sm:flex items-center gap-2 w-full sm:w-auto">
            <div className="relative w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                className="h-[44px] sm:h-[39px] px-3 w-full sm:w-auto justify-center touch-target"
                onClick={() => {
                  setIsFilterOpen(!isFilterOpen);
                  setIsSortOpen(false);
                }}
              >
                <Filter size={16} /> Filter
              </Button>
              {isFilterOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsFilterOpen(false)}
                  />
                  <div className="absolute left-0 sm:left-auto sm:right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-1 min-w-[120px] w-full sm:w-max animate-in fade-in zoom-in duration-150">
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                      Status
                    </div>
                    <button
                      onClick={() => {
                        setStatusFilter("all");
                        setIsFilterOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        statusFilter === "all" ? "bg-gray-100 font-bold" : ""
                      }`}
                    >
                      All Status
                    </button>
                    <button
                      onClick={() => {
                        setStatusFilter("active");
                        setIsFilterOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        statusFilter === "active"
                          ? "bg-gray-50 font-bold text-green-600"
                          : ""
                      }`}
                    >
                      Active
                    </button>
                    <button
                      onClick={() => {
                        setStatusFilter("inactive");
                        setIsFilterOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        statusFilter === "inactive"
                          ? "bg-gray-50 font-bold text-red-600"
                          : ""
                      }`}
                    >
                      Inactive
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="relative w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                className="h-[44px] sm:h-[39px] px-3 w-full sm:w-auto justify-center touch-target"
                onClick={() => {
                  setIsSortOpen(!isSortOpen);
                  setIsFilterOpen(false);
                }}
              >
                <ArrowUpDown size={16} /> Sort By
              </Button>
              {isSortOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsSortOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-1 min-w-[150px] animate-in fade-in zoom-in duration-150">
                    <button
                      onClick={() => {
                        handleSort("order");
                        setIsSortOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        sortField === "order" ? "bg-gray-100 font-bold" : ""
                      }`}
                    >
                      Default Order
                    </button>
                    <button
                      onClick={() => {
                        handleSort("name");
                        setIsSortOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        sortField === "name" ? "bg-gray-100 font-bold" : ""
                      }`}
                    >
                      Name
                    </button>
                    <button
                      onClick={() => {
                        handleSort("coursesCount");
                        setIsSortOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        sortField === "coursesCount"
                          ? "bg-gray-100 font-bold"
                          : ""
                      }`}
                    >
                      Course Count
                    </button>
                    <button
                      onClick={() => {
                        handleSort("createdAt");
                        setIsSortOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        sortField === "createdAt" ? "bg-gray-100 font-bold" : ""
                      }`}
                    >
                      Date Created
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {canCreate && (
            <Button
              variant="primary"
              size="lg"
              className="w-full sm:w-auto h-[48px] sm:h-[44px] touch-target"
              onClick={onAddClick}
            >
              Add new trade
            </Button>
          )}
        </div>
      </div>

      <Card className="p-0 border shadow-sm w-full overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1000px] w-full text-left text-sm border-separate border-spacing-0">
            <thead className="bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
              <tr>
                <th
                  className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100 cursor-pointer hover:text-indigo-600 transition-colors"
                  onClick={() => handleSort("order")}
                >
                  Order{" "}
                  <ArrowUpDown
                    size={14}
                    className={`inline ml-1 ${
                      sortField === "order"
                        ? "text-indigo-600"
                        : "text-gray-400"
                    }`}
                  />
                </th>

                <th
                  className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100 cursor-pointer hover:text-indigo-600 transition-colors"
                  onClick={() => handleSort("name")}
                >
                  Name{" "}
                  <ArrowUpDown
                    size={14}
                    className={`inline ml-1 ${
                      sortField === "name" ? "text-indigo-600" : "text-gray-400"
                    }`}
                  />
                </th>

                <th
                  className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100 cursor-pointer hover:text-indigo-600 transition-colors"
                  onClick={() => handleSort("coursesCount")}
                >
                  Courses{" "}
                  <ArrowUpDown
                    size={14}
                    className={`inline ml-1 ${
                      sortField === "coursesCount"
                        ? "text-indigo-600"
                        : "text-gray-400"
                    }`}
                  />
                </th>

                <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                  Status
                </th>

                <th
                  className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100 cursor-pointer hover:text-indigo-600 transition-colors"
                  onClick={() => handleSort("createdAt")}
                >
                  Created{" "}
                  <ArrowUpDown
                    size={14}
                    className={`inline ml-1 ${
                      sortField === "createdAt"
                        ? "text-indigo-600"
                        : "text-gray-400"
                    }`}
                  />
                </th>

                <th className="px-6 py-4 w-[50px] bg-gray-50 border-b border-gray-100"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    Loading trades...
                  </td>
                </tr>
              ) : paginatedTrades.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No trades found.
                  </td>
                </tr>
              ) : (
                paginatedTrades.map((trade) => (
                  <tr
                    key={trade._id}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-6 py-4 text-gray-600">
                      #{trade.order || 0}
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {trade.icon ? (
                          <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center overflow-hidden">
                            <img
                              src={trade.icon}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                            <FolderOpen size={16} />
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-[#102040]">
                            {trade.name}
                          </div>
                          {trade.slug && (
                            <div className="text-xs text-gray-400">
                              /{trade.slug}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {trade.coursesCount || 0} courses
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      {canToggle && (
                        <button
                          onClick={() => onToggleStatus(trade)}
                          className="transition-opacity hover:opacity-80"
                        >
                          {trade.isActive ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-[12px] font-semibold bg-[#E8F5EE] text-[#1B8754]">
                              <CheckCircle2 size={12} className="mr-1" />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-[12px] font-semibold bg-[#FEECEC] text-[#FF4D49]">
                              <XCircle size={12} className="mr-1" />
                              Inactive
                            </span>
                          )}
                        </button>
                      )}
                    </td>

                    <td className="px-6 py-4 text-[#989797]">
                      {new Date(trade.createdAt).toLocaleDateString()}
                    </td>

                    <td className="px-6 py-4 text-right relative">
                      <button
                        className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeMenu?.id === trade._id) {
                            setActiveMenu(null);
                          } else {
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            setActiveMenu({
                              id: trade._id,
                              position: {
                                top: rect.bottom,
                                right: window.innerWidth - rect.right,
                              },
                            });
                          }
                        }}
                      >
                        <MoreHorizontal size={20} />
                      </button>

                      <TableActionMenu
                        isOpen={activeMenu?.id === trade._id}
                        onClose={() => setActiveMenu(null)}
                        position={activeMenu?.position || null}
                      >
                        {canUpdate && (
                          <button
                            onClick={() => {
                              onEditClick(trade);
                              setActiveMenu(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                          >
                            <Edit2 size={14} className="text-blue-500" />
                            Edit Trade
                          </button>
                        )}
                        {canToggle && (
                          <button
                            onClick={() => {
                              onToggleStatus(trade);
                              setActiveMenu(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                          >
                            {trade.isActive ? (
                              <>
                                <XCircle size={14} className="text-orange-500" />
                                Deactivate
                              </>
                            ) : (
                              <>
                                <CheckCircle2
                                  size={14}
                                  className="text-green-500"
                                />
                                Activate
                              </>
                            )}
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => {
                              onDeleteClick(trade);
                              setActiveMenu(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          >
                            <Trash2 size={14} />
                            Delete
                          </button>
                        )}
                      </TableActionMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalItems > 0 && (
          <div className="px-3 sm:px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-2 text-sm text-gray-500">
            <span>
              Showing {Math.min((currentPage - 1) * pageSize + 1, totalItems)}{" "}
              to {Math.min(currentPage * pageSize, totalItems)} of {totalItems}{" "}
              trades
            </span>
            <div className="flex gap-1 ml-4">
              <button
                className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-50"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                &lt; Previous
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let p = i + 1;
                if (totalPages > 5 && currentPage > 3) {
                  p = currentPage - 2 + i;
                }
                if (p > totalPages) return null;

                return (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`px-3 py-1 rounded ${
                      currentPage === p
                        ? "bg-gray-900 text-white"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-50"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
              >
                Next &gt;
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
